import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import {
  PageHeader,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  ErrorState,
  TableEmptyState,
} from '../../components/common/pageKit';
import { useChartTokens, axisProps, gridProps, tooltipStyle } from '../../dashboard/chartTokens';
import { formatIndianNumber } from '../../utils/currency';
import crmPlusService, { LeaderboardRow } from '../../services/crmPlusService';

/**
 * Sales Leaderboard — a ranked read of the same performance rollup the
 * Territories page shows per-region, but per-rep. Deliberately restrained:
 * numbered rank badges and a quota-attainment progress bar, no medals or
 * point totals — this is an executive review surface, not a game.
 */

const isNum = (v: any): v is number => v !== null && v !== undefined && !isNaN(Number(v));
const fmtMoney = (v: any) => (isNum(v) ? formatIndianNumber(Number(v)) : '—');
const fmtPct = (v: any) => (isNum(v) ? `${Number(v).toFixed(1)}%` : '—');
const fmtInt = (v: any) => (isNum(v) ? String(Number(v)) : '—');

const fmtDate = (v?: string) => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d.getTime()) ? v : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const SalesLeaderboardPage: React.FC = () => {
  const T = useChartTokens();

  const [period, setPeriod] = useState<{ from: string; to: string } | null>(null);
  const [rows, setRows] = useState<LeaderboardRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    crmPlusService
      .getLeaderboard()
      .then((res) => {
        setPeriod(res.period || null);
        setRows(res.leaderboard || []);
      })
      .catch((e: any) => setError(e?.message || 'Failed to load the leaderboard.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const ranked = useMemo(
    () => [...rows].sort((a, b) => (Number(b.wonValue) || 0) - (Number(a.wonValue) || 0)),
    [rows]
  );

  const topThree = ranked.slice(0, 3);

  const chartData = useMemo(
    () => ranked.map((r) => ({ name: r.fullName || `#${r.userId}`, wonValue: Number(r.wonValue) || 0 })),
    [ranked]
  );

  const chartHeight = Math.max(220, chartData.length * 40 + 40);

  return (
    <Box>
      <PageHeader
        title="Sales Leaderboard"
        subtitle={
          period
            ? `Won value and quota attainment, ${fmtDate(period.from)} – ${fmtDate(period.to)}`
            : 'Won value and quota attainment by sales rep'
        }
      />

      {loading && rows.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={28} />
        </Box>
      ) : error && rows.length === 0 ? (
        <ErrorState message={error} onRetry={load} />
      ) : ranked.length === 0 ? (
        <TableSurface>
          <TableEmptyState
            title="No closed-won deals this period"
            hint="The leaderboard populates once opportunities are marked won."
          />
        </TableSurface>
      ) : (
        <>
          {/* Top 3 */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: `repeat(${Math.min(topThree.length, 3)}, minmax(0, 1fr))` },
              gap: 2,
              mb: 2.5,
            }}
          >
            {topThree.map((r, i) => {
              const attainment = isNum(r.quotaAttainmentPct) ? Math.max(0, Number(r.quotaAttainmentPct)) : null;
              return (
                <Paper
                  key={r.userId}
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: i === 0 ? T.series(0) : 'divider',
                    background:
                      i === 0 ? `linear-gradient(160deg, ${T.series(0)}0f 0%, transparent 60%)` : 'transparent',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        display: 'grid',
                        placeItems: 'center',
                        border: '1px solid',
                        borderColor: 'divider',
                        fontWeight: 700,
                        fontSize: '0.8rem',
                        color: 'text.secondary',
                        flexShrink: 0,
                      }}
                    >
                      {i + 1}
                    </Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.fullName || `User #${r.userId}`}
                    </Typography>
                  </Box>
                  <Typography
                    title={fmtMoney(r.wonValue)}
                    sx={{
                      fontSize: { xs: '1.3rem', sm: '1.5rem' },
                      fontWeight: 700,
                      color: T.series(0),
                      fontVariantNumeric: 'tabular-nums',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      mb: 0.25,
                    }}
                  >
                    {fmtMoney(r.wonValue)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    {fmtInt(r.wonCount)} deals won
                  </Typography>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Quota Attainment
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      {fmtPct(r.quotaAttainmentPct)}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={attainment !== null ? Math.min(attainment, 100) : 0}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: `${T.series(0)}1f`,
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: attainment !== null && attainment >= 100 ? T.status.good : T.series(0),
                        borderRadius: 3,
                      },
                    }}
                  />
                </Paper>
              );
            })}
          </Box>

          {/* Won value by rep */}
          <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
              Won Value by Rep
            </Typography>
            <Box sx={{ width: '100%', height: chartHeight }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartData}
                  layout="vertical"
                  margin={{ top: 4, right: 24, left: 0, bottom: 4 }}
                >
                  <CartesianGrid {...gridProps(T)} horizontal={false} />
                  <XAxis type="number" {...axisProps(T)} tickFormatter={(v: number) => formatIndianNumber(v)} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    {...axisProps(T)}
                    width={140}
                    tick={{ fill: T.textSecondary, fontSize: 12 }}
                  />
                  <Tooltip {...tooltipStyle(T)} formatter={(v: any) => formatIndianNumber(Number(v))} />
                  <Bar dataKey="wonValue" radius={[0, 4, 4, 0]}>
                    {chartData.map((_, i) => (
                      <Cell key={i} fill={T.series(i)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>

          {/* Full ranked table */}
          <TableSurface>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {[
                      'Rank',
                      'Rep',
                      'Won Value',
                      'Won Count',
                      'Win Rate',
                      'Open Pipeline',
                      'Weighted',
                      'Quota Attainment',
                      'Avg Deal Size',
                    ].map((h, i) => (
                      <TableCell key={h} align={i <= 1 ? 'left' : 'right'} sx={headerCellSx}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ranked.map((r, i) => (
                    <TableRow key={r.userId} sx={bodyRowSx}>
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                          {i + 1}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {r.fullName || `User #${r.userId}`}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{fmtMoney(r.wonValue)}</TableCell>
                      <TableCell align="right">{fmtInt(r.wonCount)}</TableCell>
                      <TableCell align="right">{fmtPct(r.winRatePct)}</TableCell>
                      <TableCell align="right">{fmtMoney(r.openPipeline)}</TableCell>
                      <TableCell align="right">{fmtMoney(r.weightedPipeline)}</TableCell>
                      <TableCell align="right">{fmtPct(r.quotaAttainmentPct)}</TableCell>
                      <TableCell align="right">{fmtMoney(r.avgDealSize)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TableSurface>
        </>
      )}
    </Box>
  );
};

export default SalesLeaderboardPage;
